import type { ComponentChildren } from "preact";
import type { Field, ObjEntity } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { ListView } from "../../lib/cardView.ts";
import { FOLDER_GROUP } from "../../lib/folders.ts";
import { setQuery } from "../../lib/router.ts";
import { Select, Switch } from "../../ui/form.tsx";
import { DisplayIcon, ExpandIcon } from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { buttonClass } from "../../ui/styles.ts";
import { Toolbar, type ToolbarItem } from "../../ui/Toolbar.tsx";
import { ViewToggle } from "../../ui/ViewToggle.tsx";

/** Sort orders: the fixed ones, then each sortable field both ways. */
function sortOptions(entity: ObjEntity | undefined) {
  const fields = (entity?.fields ?? []).filter((f) => f.type !== "ref");
  return [
    { value: "-created", label: t("items.sort.newest") },
    { value: "created", label: t("items.sort.oldest") },
    { value: "-updated", label: t("items.sort.updated") },
    { value: "title", label: t("items.sort.titleAsc") },
    { value: "-title", label: t("items.sort.titleDesc") },
    ...fields.flatMap((f) => [
      { value: `data.${f.key}`, label: t("items.sort.asc", { field: f.label || f.key }) },
      { value: `-data.${f.key}`, label: t("items.sort.desc", { field: f.label || f.key }) },
    ]),
  ];
}

/**
 * The choice of view (table, cards, and with a choice field to group by, a board), the board's grouping, and (cards
 * have no headers to click) the sort order.
 */
export function ListBar(p: {
  entity: ObjEntity | undefined;
  sort: string;
  view: ListView;
  onView: (v: ListView) => void;
  /** What the board groups by: a choice field's key, or `FOLDER_GROUP`. */
  group: string | undefined;
  groups: readonly Field[];
  /** The board can group by folder (there are folders). */
  byFolder: boolean;
  /** Whether conversations show as one row each. */
  conversations: boolean;
  /** No conversation to group here (no emails, or none with others): the switch is there, off limits. */
  noConversations: boolean;
  onConversations: (on: boolean) => void;
  /** Whether what's in subfolders is listed too, flat (null: no folders, off limits). */
  deep: boolean | null;
  onDeep: (on: boolean) => void;
  /** Whether a filtered list's folders include those holding no match (null: not filtered, or no folders: off
   *  limits). */
  unmatched: boolean | null;
  onUnmatched: (on: boolean) => void;
  /** While rows are selected: what to do with them, in place of the grouping and the view (no layout jump). */
  selection?: ComponentChildren;
}) {
  const views: ListView[] = p.groups.length > 0 || p.byFolder ? ["table", "cards", "board"] : ["table", "cards"];
  // How the list is shown: every switch there, those that don't apply here off limits.
  const switches = [
    { key: "deep", label: t("folders.flatten"), on: p.deep ?? false, set: p.onDeep, disabled: p.deep === null },
    {
      key: "unmatched",
      label: t("folders.unmatched"),
      on: p.unmatched ?? false,
      set: p.onUnmatched,
      disabled: p.unmatched === null,
    },
    {
      key: "conversations",
      label: t("items.conversations"),
      on: p.conversations,
      set: p.onConversations,
      disabled: p.noConversations,
    },
  ];
  const items: ToolbarItem[] = [
    ...(p.view === "board" && p.group !== undefined
      ? [
          {
            key: "group",
            node: (
              <Select
                aria-label={t("items.board.groupBy")}
                value={p.group}
                onValue={(v) => setQuery({ group: v })}
                options={[
                  ...p.groups.map((f) => ({
                    value: f.key,
                    label: t("items.board.groupByField", { field: f.label || f.key }),
                  })),
                  ...(p.byFolder ? [{ value: FOLDER_GROUP, label: t("items.board.byFolder") }] : []),
                ]}
              />
            ),
            class: "flex-1 sm:max-w-56",
            min: SELECT_MIN_PX,
            drop: 10,
          },
        ]
      : []),
    ...(p.view === "table"
      ? []
      : [
          {
            key: "sort",
            node: (
              <Select
                aria-label={t("items.sort.label")}
                value={p.sort}
                onValue={(v) => setQuery({ sort: v, cursor: null })}
                options={sortOptions(p.entity)}
              />
            ),
            class: "flex-1 sm:max-w-64",
            min: SELECT_MIN_PX,
          },
        ]),
    // On the right, together: how it's shown (a menu of switches), then the view (or what the selection can have done,
    // taking the rest).
    ...(p.selection
      ? [
          {
            key: "selection",
            // Where the view was, in the iOS app grown from it (lib/selection.ts).
            node: (
              <div data-select="end" class="flex min-w-0">
                {p.selection}
              </div>
            ),
            shrinks: true,
            end: true,
          },
        ]
      : [
          {
            key: "display",
            node: (
              <Menu
                label={
                  <>
                    <DisplayIcon />
                    {t("items.display")}
                    <ExpandIcon />
                  </>
                }
                class={buttonClass("secondary", "sm")}
                items={switches.map((w) => ({
                  key: w.key,
                  content: () => (
                    <Switch checked={w.on} label={w.label} onCheck={w.set} disabled={w.disabled} class="py-0.5" />
                  ),
                }))}
              />
            ),
            end: true,
            drop: 20,
            menu: switches
              .filter((w) => !w.disabled)
              .map((w) => ({
                label: w.label,
                current: w.on,
                group: t("items.display"),
                onSelect: () => w.set(!w.on),
              })),
          },
          // Its gap from the switch as wide as before (gap-4).
          {
            key: "view",
            // What the selection's bar grows from, in the iOS app (lib/selection.ts).
            node: (
              <div data-select="end" class="flex">
                <ViewToggle views={views} view={p.view} onView={p.onView} />
              </div>
            ),
            end: true,
            class: "pl-2",
          },
        ]),
  ];
  return (
    <div class="mb-3 flex">
      <Toolbar items={items} />
    </div>
  );
}

/** The least a select (sorting, grouping) takes. */
const SELECT_MIN_PX = 128;
